// BrimLight/client/ui/VideoEditor.js

import { ComponentBase } from "../core/ComponentBase.js";
import { VideoPreviewer } from "../helpers/video-editor/VideoPreviewer.js";
import { Timeline } from "../helpers/video-editor/Timeline.js";
import { getClipEditorFormConfig } from "../helpers/video-editor/ClipEditorFormConfig.js";
import { getStoryEditorFormConfig } from "../helpers/video-editor/StoryEditorFormConfig.js";
import { getProjectSettingsFormConfig } from "../helpers/video-editor/ProjectSettingsFormConfig.js";
import { TrackSettingsPanel } from "../helpers/video-editor/TrackSettingsPanel.js";
import { AIModifyModal } from "../helpers/video-editor/AIModifyModal.js";
import { AIProgressMonitor } from "../helpers/video-editor/AIProgressMonitor.js"; // <-- NEW IMPORT


export class VideoEditor extends ComponentBase {
    static rdsSubscriptions = ['clips', 'projectSettings', 'characters', 'environments', 'props'];

    static styles = `
        .video-editor-layout {
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
            overflow: hidden;
            background-color: var(--bs-body-bg);
            gap: 0.5rem;
            position: relative;
        }
        .editor-content-wrapper {
            display: flex;
            flex-direction: column;
            height: 100%;
            gap: 0.5rem;
        }
        .preview-container {
            flex-shrink: 0;
            flex-grow: 1;
            min-height: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            background-color: #000;
            border-radius: var(--bs-border-radius);
        }
        .timeline-container {
            flex-shrink: 0;
            height: 220px; /* Slightly smaller for mobile */
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        .editor-state-overlay {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--bs-body-bg);
            z-index: 10;
            padding: 1rem;
        }
        .generate-assets-modal .asset-list {
            max-height: 60vh;
            overflow-y: auto;
        }
        /* Responsive adjustments for larger screens */
        @media (min-width: 768px) {
            .video-editor-layout {
            }
            .timeline-container {
                height: 240px;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.currentProjectId = null;
        this.isLoading = false;
        this.projectSettings = null;
        this.orderedClips = [];
        this.selectedClipId = null;
        this.currentTime = 0;
        this.isPlaying = false;
        this.animationFrameId = null;
        this.previewer = null;
        this.timeline = null;
        this.clipEditModal = null;
        this.storyModalForm = null;
        this.settingsModalForm = null;
        this.editorContentWrapper = null;
        this.stateOverlay = null;
        this.audioPlayer = new Audio();
        this.previewModalInstance = null;
        this._boundHandleProjectSelected = this.handleProjectSelected.bind(this);
        this.trackSettingsPanel = null;
        this.aiModifyModal = null;
        this.aiProgressMonitor = null;

        this.applyStyles();
    }


    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'video-editor-layout';
        this.renderInitialStructure();
        const childConfig = { appClient: this.appClient };
        this.previewer = new VideoPreviewer(childConfig);
        this.timeline = new Timeline(childConfig);
        this.trackSettingsPanel = new TrackSettingsPanel(childConfig);
        this.aiModifyModal = new AIModifyModal({ ...childConfig, instanceId: 'ai-modify-modal' });
        this.aiProgressMonitor = new AIProgressMonitor({ ...childConfig, instanceId: 'ai-progress-monitor' });

        await Promise.all([
            this.previewer.initialize(),
            this.timeline.initialize(),
            this.trackSettingsPanel.initialize(),
            this.aiModifyModal.initialize(),
            this.aiProgressMonitor.initialize()

        ]);
        this.mountChildComponents();
        this.setupEventListeners();
        this.appClient.events.local.on('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.server.on('agentic-job-progress', this.handleJobProgress.bind(this));

        this._boundHandleKeyDown = this.handleKeyDown.bind(this);
        document.addEventListener('keydown', this._boundHandleKeyDown);

        await super.initialize();
    }

    render() {
        return this.container;
    }


    renderInitialStructure() {
        this.container.innerHTML = `
            <div class="editor-state-overlay" style="display: none;"></div>
            <div class="editor-content-wrapper">
                <div class="preview-container"></div>
                <div class="timeline-container"></div>
            </div>
        `;
        this.editorContentWrapper = this.container.querySelector('.editor-content-wrapper');
        this.stateOverlay = this.container.querySelector('.editor-state-overlay');
    }



    mountChildComponents() {
        const previewContainer = this.container.querySelector('.preview-container');
        const timelineContainer = this.container.querySelector('.timeline-container');
        previewContainer.appendChild(this.previewer.container);
        timelineContainer.appendChild(this.timeline.container);
        this.container.appendChild(this.trackSettingsPanel.container);
        this.container.appendChild(this.aiProgressMonitor.container);

    }

    updateUI() {
        if (!this.currentProjectId) {
            this.editorContentWrapper.style.display = 'none';
            this.stateOverlay.style.display = 'flex';
            this.stateOverlay.innerHTML = `<div class="alert alert-info text-center m-3"><i class="bi bi-info-circle-fill me-2"></i> ${this.t('Please select a project to open the editor.')}</div>`;
            return;
        }
        if (this.isLoading) {
            this.editorContentWrapper.style.display = 'none';
            this.stateOverlay.style.display = 'flex';
            this.stateOverlay.innerHTML = `<div class="d-flex justify-content-center align-items-center h-100"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>`;
            return;
        }
        this.stateOverlay.style.display = 'none';
        this.editorContentWrapper.style.display = 'flex';
        this.previewer.setData(this.orderedClips, this.projectSettings);

        // --- START OF FIX: Pass projectSettings to the timeline ---
        this.timeline.setData(this.orderedClips, this.selectedClipId, this.projectSettings);
        // --- END OF FIX ---

        this.updatePlaybackTime(this.currentTime);
    }

    setupEventListeners() {
        this.timeline.on('play', () => this.play());
        this.timeline.on('pause', () => this.pause());
        this.timeline.on('clipSelected', (clipId) => {
            this.selectedClipId = clipId;
            this.timeline.setData(this.orderedClips, this.selectedClipId, this.projectSettings);
        });
        // OLD: this.timeline.on('reorganizeProjectTracks', () => this.handleReorganizeTracks());
        this.timeline.on('seek', (time) => this.updatePlaybackTime(time));
        this.timeline.on('addClipClicked', () => this.showEditClipModal(null));
        this.timeline.on('updateClipBatch', async ({ updates }) => {
            try {
                await this.appClient.rpcClient.call('ClipRpc.updateMany', { updates: updates });
                this.appClient.notifier.info("Timeline saved.");
            } catch (error) {
                this.appClient.notifier.error("Failed to save timeline changes.");
                this.loadData();
            }
        });
        this.timeline.on('editClip', ({ clipId }) => this.showEditClipModal(clipId));
        this.timeline.on('previewClip', ({ clip }) => {
            if (clip.type === 'visual') this.showPreviewModal(clip.assetPath, clip.assetHash);
            else {
                const dummyEvent = { currentTarget: document.createElement('button') };
                dummyEvent.currentTarget.innerHTML = '<i class="bi bi-play-circle-fill"></i>';
                this.handleAudioPreview(dummyEvent, clip.assetPath);
            }
        });
        this.timeline.on('deleteClip', ({ clipId }) => this.handleDeleteClip(clipId));
        this.timeline.on('generateClipAsset', ({ clip, force }) => this.handleGenerateClipAsset(clip._id, force));
        this.timeline.on('generateVideoAnimation', ({ clipId }) => this.handleGenerateVideoAnimation(clipId));
        this.timeline.on('editStory', () => this.showStoryModal());
        this.timeline.on('generateAllAssets', () => this.showGenerateAssetsModal());
        this.timeline.on('generateAnimations', () => this.showGenerateAnimationsModal());
        this.timeline.on('editProjectSettings', () => this.showProjectSettingsModal());
        this.timeline.on('generateVideo', () => this.showGeneratedVideosModal());
        this.timeline.on('showTimelineTools', () => this.showTimelineToolsModal());
        this.timeline.on('resizeClipToMedia', ({ clipId }) => this.handleResizeClipToMedia(clipId));

        this.timeline.on('aiModifyProject', () => {
            if (this.aiModifyModal) {
                this.aiModifyModal.show(this.timeline.totalDuration, this.currentTime);
            }
        });

        this.timeline.on('updateAllClipDurations', () => this.handleUpdateAllClipDurations());
        this.aiModifyModal.on('submit', (formData) => this.handleAiModifyProject(formData));
        this.aiProgressMonitor.on('cancel-job', (jobId) => this.handleCancelAiJob(jobId));

        this.timeline.on('trackSettingsClicked', () => {
            this.trackSettingsPanel.setData(this.projectSettings?.trackSettings);
            this.trackSettingsPanel.show();
        });

        this.trackSettingsPanel.on('trackSettingsChanged', ({ trackSettings, save = false }) => {
            if (this.projectSettings) {
                this.projectSettings.trackSettings = trackSettings;
                if (save) {
                    this.saveTrackSettings();
                }
            }
        });

        this.trackSettingsPanel.on('trackSettingsChangeDebounced', () => {
            this.saveTrackSettings();
        });
    }


    async saveTrackSettings() {
        if (!this.projectSettings) return;
        try {
            await this.appClient.rpcClient.call('ProjectSettingsRpc.saveSettings', {
                projectId: this.currentProjectId,
                ...this.projectSettings
            });
        } catch (error) {
            this.appClient.notifier.error(`Failed to save track settings: ${error.message}`);
        }
    }


    handleKeyDown(e) {
        // Ignore key presses if an input, textarea, or modal is focused
        const activeEl = document.activeElement;
        const isEditing = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.closest('.modal'));
        if (isEditing) return;

        switch (e.key) {
            case ' ': // Spacebar
                e.preventDefault();
                this.isPlaying ? this.pause() : this.play();
                break;

            case 'ArrowLeft':
                e.preventDefault();
                this.updatePlaybackTime(Math.max(0, this.currentTime - 1)); // Seek back 1 second
                break;

            case 'ArrowRight':
                e.preventDefault();
                const totalDuration = this.orderedClips.reduce((sum, clip) => Math.max(sum, clip.startTime + clip.duration), 0);
                this.updatePlaybackTime(Math.min(totalDuration, this.currentTime + 1)); // Seek forward 1 second
                break;

            case 'ArrowUp': {
                e.preventDefault();
                // --- START OF FIX: Seek only between VISUAL clips ---
                const visualClips = this.orderedClips.filter(c => c.type === 'visual');
                // Find the last visual clip that starts before the current time
                const prevClip = [...visualClips].reverse().find(c => c.startTime < this.currentTime - 0.1);
                if (prevClip) {
                    this.updatePlaybackTime(prevClip.startTime);
                } else {
                    this.updatePlaybackTime(0); // Go to start if no previous clip
                }
                // --- END OF FIX ---
                break;
            }

            case 'ArrowDown': {
                e.preventDefault();
                // --- START OF FIX: Seek only between VISUAL clips ---
                const visualClips = this.orderedClips.filter(c => c.type === 'visual');
                // Find the first visual clip that starts after the current time
                const nextClip = visualClips.find(c => c.startTime > this.currentTime + 0.1);
                if (nextClip) {
                    this.updatePlaybackTime(nextClip.startTime);
                }
                // --- END OF FIX ---
                break;
            }
        }
    }



    async handleGenerateVideo() {
        if (!this.currentProjectId) {
            this.appClient.notifier.warn("Please select a project first.");
            return;
        }

        // 1. Client-side validation: Check if all clips have assets.
        const clipsWithoutAssets = this.orderedClips.filter(clip => {
            const assetRequired = ['visual', 'dialogue', 'sfx', 'bgMusic', 'ambient'].includes(clip.type);
            return assetRequired && !clip.assetPath;
        });

        if (clipsWithoutAssets.length > 0) {
            const clipTypes = [...new Set(clipsWithoutAssets.map(c => c.type))].join(', ');
            this.appClient.notifier.error(`Cannot generate video. The following clip types are missing assets: ${clipTypes}. Please use "Generate All Assets" first.`);
            return;
        }

        this.appClient.notifier.info("Starting video generation process...");

        // 2. Setup progress listener and call RPC
        const clientInstanceId = `video-gen-${this.currentProjectId}-${Date.now()}`;

        this.appClient.notifier.showProgress({
            title: 'Generating Video',
            message: 'Preparing video generation job...',
            isDialog: true
        });

        const progressHandler = (eventPayload) => {
            if (eventPayload.instanceId !== clientInstanceId) return;

            if (eventPayload.type === 'progress') {
                this.appClient.notifier.setProgress({
                    progress: eventPayload.data.progress,
                    message: eventPayload.progressMessage
                });
            } else if (eventPayload.type === 'process-end') {
                this.appClient.notifier.hideProgress();
                this.appClient.events.server.off('video-generation-progress', progressHandler);
                if (eventPayload.code === 0) {
                    this.appClient.notifier.success(eventPayload.progressMessage);
                    // Maybe show a link to the video?
                    if (eventPayload.data?.videoPath) {
                        const videoUrl = `/${eventPayload.data.videoPath}`;
                        this.appClient.notifier.notify(`Video ready! <a href="${videoUrl}" target="_blank">Click to view</a>`, {
                            type: 'success',
                            duration: 10000,
                            isHtml: true
                        });
                    }
                } else {
                    this.appClient.notifier.error(eventPayload.progressMessage);
                }
            }
        };

        this.appClient.events.server.on('video-generation-progress', progressHandler);

        try {
            await this.appClient.rpcClient.call('VideoGeneratorRpc.generateProjectVideo', {
                projectId: this.currentProjectId
            }, { instanceId: clientInstanceId });
        } catch (error) {
            this.appClient.notifier.hideProgress();
            this.appClient.events.server.off('video-generation-progress', progressHandler);
            this.appClient.notifier.error(`Failed to start video generation: ${error.message}`);
        }
    }

    async handleGenerateVideoAnimation(clipId) {
        this.appClient.notifier.info(`Starting video animation generation for clip...`);
        try {
            await this.appClient.rpcClient.call('VideoAnimationGeneratorRpc.generateAnimationForClip', { clipId });
            this.appClient.notifier.success(`Video animation for clip generated successfully.`);
            // RDS will handle the UI update automatically.
        } catch (error) {
            this.appClient.notifier.error(`Failed to generate video animation: ${error.message}`);
        }
    }

    async showStoryModal() {
        if (this.storyModalForm) this.storyModalForm.destroy();

        const formConfig = getStoryEditorFormConfig();
        this.storyModalForm = await this.createManagedComponent("DataForm", document.body, {
            ...formConfig,
            instanceId: `story-editor-modal-form-${this.currentProjectId}`,
            layout: 'dialog',
            title: 'Story Details', // This adds the header
            actions: { save: true, cancel: true } // This adds the cancel button
        });

        this.storyModalForm.show(); // Show modal with spinner

        // This event listener handles the "Cancel" button and the 'X' in the header
        this.storyModalForm.on('cancel', () => {
            if (this.storyModalForm) {
                this.storyModalForm.destroy();
                this.storyModalForm = null;
            }
        });

        try {
            const storyData = await this.appClient.rpcClient.call('StoryRpc.getStory', { projectId: this.currentProjectId });
            this.storyModalForm.setData(storyData);
        } catch (error) {
            this.appClient.notifier.error("Could not load story data.");
            this.storyModalForm.hide(); // This will trigger the 'cancel' event and cleanup
        }

        this.storyModalForm.on('dataUpdated', async (updatedData) => {
            try {
                await this.appClient.rpcClient.call('StoryRpc.saveStory', { projectId: this.currentProjectId, ...updatedData });
                this.appClient.notifier.success("Story saved successfully.");
                this.storyModalForm.hide(); // This will trigger the 'cancel' event and cleanup
            } catch (error) {
                this.appClient.notifier.error(`Failed to save story: ${error.message}`);
            }
        });
    }

    async showProjectSettingsModal() {
        if (this.settingsModalForm) this.settingsModalForm.destroy();

        const formConfig = getProjectSettingsFormConfig();
        this.settingsModalForm = await this.createManagedComponent("DataForm", document.body, {
            ...formConfig,
            instanceId: `settings-editor-modal-form-${this.currentProjectId}`,
            layout: 'dialog',
            title: 'Project Settings', // This adds the header
            actions: { save: true, cancel: true } // This adds the cancel button
        });

        this.settingsModalForm.show();

        // This event listener handles the "Cancel" button and the 'X' in the header
        this.settingsModalForm.on('cancel', () => {
            if (this.settingsModalForm) {
                this.settingsModalForm.destroy();
                this.settingsModalForm = null;
            }
        });

        try {
            const settingsData = await this.appClient.rpcClient.call('ProjectSettingsRpc.getSettings', { projectId: this.currentProjectId });
            this.settingsModalForm.setData(settingsData);
        } catch (error) {
            this.appClient.notifier.error("Could not load project settings.");
            this.settingsModalForm.hide(); // This will trigger the 'cancel' event and cleanup
        }

        this.settingsModalForm.on('dataUpdated', async (updatedData) => {
            try {
                await this.appClient.rpcClient.call('ProjectSettingsRpc.saveSettings', { projectId: this.currentProjectId, ...updatedData });
                this.appClient.notifier.success("Settings saved successfully.");
                this.settingsModalForm.hide(); // This will trigger the 'cancel' event and cleanup
            } catch (error) {
                this.appClient.notifier.error(`Failed to save settings: ${error.message}`);
            }
        });
    }

    async handleAiModifyProject(formData) {
        if (!this.currentProjectId) return;

        this.appClient.notifier.info("Starting AI Editor job...", { duration: 3000 });
        try {
            const result = await this.appClient.rpcClient.call('StoryRpc.modifyStoryForExistingProject', {
                projectId: this.currentProjectId,
                ...formData
            });
            this.aiProgressMonitor.show(result.jobId, `AI Editor: ${formData.modificationType}`);
        } catch (error) {
            this.log.error("Failed to start AI Editor job:", error);
            this.appClient.notifier.error(`AI Editor failed to start: ${error.message}`);
        }
    }


    async handleUpdateAllClipDurations() {
        if (!this.currentProjectId) return;
        this.appClient.notifier.info("Updating all clip durations. This may take a moment...");
        try {
            const result = await this.appClient.rpcClient.call('AssetGenerationRpc.updateAllClipDurations', {
                projectId: this.currentProjectId
            });
            this.appClient.notifier.success(result.message || "Clip durations updated.");
            // RDS will automatically refresh the data, no need to call this.loadData()
        } catch (error) {
            this.log.error("Failed to update clip durations:", error);
            this.appClient.notifier.error(`Failed to update durations: ${error.message}`);
        }
    }


    async showGenerateAssetsModal() {
        const modalId = `generate-assets-modal-${this.config.instanceId}`;
        document.getElementById(modalId)?.remove();

        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade generate-assets-modal';
        modalEl.id = modalId;
        modalEl.setAttribute('tabindex', '-1');
        modalEl.innerHTML = `
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header"><h5 class="modal-title">Generate Project Assets</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button></div>
                    <div class="modal-body"><div class="d-flex justify-content-center p-5"><div class="spinner-border" role="status"></div></div></div>
                    <div class="modal-footer">
                        <div class="form-check form-switch me-auto"><input class="form-check-input" type="checkbox" id="force-regen-switch"><label class="form-check-label" for="force-regen-switch">Force Regeneration</label></div>
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="start-generation-btn">Generate Selected</button>
                    </div>
                </div>
            </div>`;
        document.body.appendChild(modalEl);
        const bsModal = new bootstrap.Modal(modalEl);
        bsModal.show();

        const body = modalEl.querySelector('.modal-body');
        try {
            const clips = await this.appClient.rds.query('clips', c => c.projectId === this.currentProjectId);
            const characterIds = new Set(clips.flatMap(c => c.characterIds || []));
            const environmentIds = new Set(clips.map(c => c.environmentId).filter(Boolean));
            const propIds = new Set(clips.flatMap(c => c.propIds || []));

            const [allCharacters, allEnvironments, allProps] = await Promise.all([
                this.appClient.rds.getAll('characters'),
                this.appClient.rds.getAll('environments'),
                this.appClient.rds.getAll('props')
            ]);

            const projectCharacters = allCharacters.filter(c => characterIds.has(c._id));
            const projectEnvironments = allEnvironments.filter(e => environmentIds.has(e._id));
            const projectProps = allProps.filter(p => propIds.has(p._id));

            // --- FIX: Correctly calculate `needsGen` to include the `isDirty` flag ---
            const needsGen = (asset) => !asset.assetPath || asset.isDirty;
            const needsGenGlobal = (asset) => !asset.representativeImageRelativePath || asset.isDirty;

            const assetGroups = {
                character: { label: 'Characters', assets: projectCharacters.map(c => ({ ...c, assetType: 'character', needsGen: needsGenGlobal(c) })) },
                environment: { label: 'Environments', assets: projectEnvironments.map(e => ({ ...e, assetType: 'environment', needsGen: needsGenGlobal(e) })) },
                prop: { label: 'Props', assets: projectProps.map(p => ({ ...p, assetType: 'prop', needsGen: needsGenGlobal(p) })) },
                visual: { label: 'Visual Clips', assets: clips.filter(c => c.type === 'visual').map(c => ({ ...c, assetType: 'clip', needsGen: needsGen(c) })) },
                dialogue: { label: 'Dialogue Clips', assets: clips.filter(c => c.type === 'dialogue').map(c => ({ ...c, assetType: 'clip', needsGen: needsGen(c) })) },
                sfx: { label: 'SFX Clips', assets: clips.filter(c => c.type === 'sfx').map(c => ({ ...c, assetType: 'clip', needsGen: needsGen(c) })) },
                bgMusic: { label: 'Music Clips', assets: clips.filter(c => c.type === 'bgMusic').map(c => ({ ...c, assetType: 'clip', needsGen: needsGen(c) })) },
                ambient: { label: 'Ambient Clips', assets: clips.filter(c => c.type === 'ambient').map(c => ({ ...c, assetType: 'clip', needsGen: needsGen(c) })) },
            };

            body.innerHTML = `
                <div class="mb-3"><button class="btn btn-sm btn-outline-secondary" id="select-all-assets">Select All</button> <button class="btn btn-sm btn-outline-secondary" id="deselect-all-assets">Deselect All</button></div>
                <ul class="list-group asset-list">
                    ${Object.entries(assetGroups).filter(([_, group]) => group.assets.length > 0).map(([key, group]) => {
                const needsGenCount = group.assets.filter(a => a.needsGen).length;
                const isChecked = needsGenCount > 0;
                return `
                        <li class="list-group-item">
                            <input class="form-check-input me-2" type="checkbox" value="" id="group-${key}" data-group-key="${key}" ${isChecked ? 'checked' : ''}>
                            <label class="form-check-label" for="group-${key}">
                                <strong>${group.label}</strong> (${group.assets.length} total)
                                <span class="badge bg-${needsGenCount > 0 ? 'warning text-dark' : 'success'} ms-2">${needsGenCount} need generation</span>
                            </label>
                        </li>`;
            }).join('')}
                </ul>`;

            modalEl.querySelector('#select-all-assets').addEventListener('click', () => modalEl.querySelectorAll('.form-check-input').forEach(cb => cb.checked = true));
            modalEl.querySelector('#deselect-all-assets').addEventListener('click', () => modalEl.querySelectorAll('.form-check-input').forEach(cb => cb.checked = false));

            modalEl.querySelector('#start-generation-btn').addEventListener('click', () => {
                const assetsToGenerate = [];
                const forceRegeneration = modalEl.querySelector('#force-regen-switch').checked;

                modalEl.querySelectorAll('.form-check-input:checked').forEach(cb => {
                    const groupKey = cb.dataset.groupKey;
                    if (assetGroups[groupKey]) {
                        const assetsInGroup = assetGroups[groupKey].assets;
                        const assetsToPush = forceRegeneration
                            ? assetsInGroup
                            : assetsInGroup.filter(asset => asset.needsGen);

                        assetsToPush.forEach(asset => {
                            // --- FIX: Send the specific clip type (`subType`) to the server ---
                            assetsToGenerate.push({
                                id: asset._id,
                                type: asset.assetType, // This will be 'character', 'prop', or 'clip'
                                subType: asset.assetType === 'clip' ? asset.type : null // This will be 'dialogue', 'visual', etc.
                            });
                        });
                    }
                });

                if (assetsToGenerate.length === 0) {
                    this.appClient.notifier.warn("No assets need generation. To regenerate existing assets, check 'Force Regeneration'.");
                    return;
                }

                bsModal.hide();
                const clientInstanceId = `asset-gen-${this.currentProjectId}-${Date.now()}`;
                this.appClient.notifier.showProgress({
                    title: 'Generating Project Assets',
                    message: 'Preparing to start generation...',
                    isDialog: true
                });

                const progressHandler = (eventPayload) => {
                    if (eventPayload.instanceId !== clientInstanceId) return;
                    if (eventPayload.type === 'progress') {
                        this.appClient.notifier.setProgress({ progress: eventPayload.data.progress, message: eventPayload.progressMessage });
                    } else if (eventPayload.type === 'process-end') {
                        this.appClient.notifier.hideProgress();
                        this.appClient.events.server.off('asset-generation-progress', progressHandler);
                        if (eventPayload.code === 0) {
                            this.appClient.notifier.success(eventPayload.progressMessage);
                        } else {
                            this.appClient.notifier.error(eventPayload.progressMessage);
                        }
                    }
                };

                this.appClient.events.server.on('asset-generation-progress', progressHandler);
                this.appClient.rpcClient.call('AssetGenerationRpc.generateProjectAssetsWithProgress', {
                    projectId: this.currentProjectId,
                    assetsToGenerate,
                    forceRegeneration
                }, { instanceId: clientInstanceId });
            });

        } catch (e) {
            this.log.error("Error preparing asset generation modal:", e);
            body.innerHTML = `<div class="alert alert-danger">Could not load project assets to generate.</div>`;
        }
        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });
    }



    async showGeneratedVideosModal() {
        if (!this.currentProjectId) {
            this.appClient.notifier.warn("Please select a project first.");
            return;
        }

        const modalId = `generated-videos-modal-${this.config.instanceId}`;
        document.getElementById(modalId)?.remove();

        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade';
        modalEl.id = modalId;
        modalEl.setAttribute('tabindex', '-1');
        modalEl.innerHTML = `
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Generated Videos</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="text-center p-5"><div class="spinner-border" role="status"></div></div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                        <button type="button" class="btn btn-primary" id="generate-new-video-btn">
                            <i class="bi bi-film me-2"></i> Generate New Video
                        </button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modalEl);
        const bsModal = new bootstrap.Modal(modalEl);
        bsModal.show();

        const body = modalEl.querySelector('.modal-body');
        const generateBtn = modalEl.querySelector('#generate-new-video-btn');

        const renderJobs = (jobs) => {
            if (jobs.length === 0) {
                body.innerHTML = `<div class="text-center p-4 text-muted">No videos have been generated for this project yet.</div>`;
                return;
            }

            body.innerHTML = `
                <ul class="list-group list-group-flush">
                    ${jobs.map(job => {
                let statusBadge = '';
                let actions = '';
                switch (job.status) {
                    case 'completed':
                        statusBadge = `<span class="badge bg-success">Completed</span>`;
                        actions = `
                                    <a href="/${job.finalVideoRelativePath}" target="_blank" class="btn btn-sm btn-outline-primary" title="Download"><i class="bi bi-download"></i></a>
                                    <button class="btn btn-sm btn-outline-danger" data-action="delete" data-job-id="${job._id}" title="Delete"><i class="bi bi-trash"></i></button>
                                `;
                        break;
                    case 'in_progress':
                        statusBadge = `<span class="badge bg-info text-dark">In Progress (${job.progress || 0}%)</span>`;
                        actions = `<button class="btn btn-sm btn-outline-warning" data-action="cancel" data-job-id="${job._id}" title="Cancel"><i class="bi bi-stop-circle"></i></button>`;
                        break;
                    case 'failed':
                        statusBadge = `<span class="badge bg-danger" title="${job.progressMessage || 'An error occurred.'}">Failed</span>`;
                        actions = `<button class="btn btn-sm btn-outline-danger" data-action="delete" data-job-id="${job._id}" title="Delete"><i class="bi bi-trash"></i></button>`;
                        break;
                    case 'cancelled':
                        statusBadge = `<span class="badge bg-secondary">Cancelled</span>`;
                        actions = `<button class="btn btn-sm btn-outline-danger" data-action="delete" data-job-id="${job._id}" title="Delete"><i class="bi bi-trash"></i></button>`;
                        break;
                    default:
                        statusBadge = `<span class="badge bg-light text-dark">${job.status}</span>`;
                }

                // --- FIX FOR DATE DISPLAY ---
                const createdAtDate = job.createdAt ? new Date(job.createdAt).toLocaleString() : 'N/A';

                return `
                            <li class="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <div class="fw-bold">${createdAtDate}</div>
                                    <small class="text-muted">${job.progressMessage || job.status}</small>
                                </div>
                                <div class="d-flex align-items-center gap-2">
                                    ${statusBadge}
                                    <div class="btn-group btn-group-sm">${actions}</div>
                                </div>
                            </li>
                        `;
            }).join('')}
                </ul>
            `;
        };

        const loadJobs = async () => {
            try {
                const jobs = await this.appClient.rpcClient.call('VideoGeneratorRpc.listJobs', { projectId: this.currentProjectId });
                renderJobs(jobs);
            } catch (error) {
                body.innerHTML = `<div class="alert alert-danger">Could not load video history: ${error.message}</div>`;
            }
        };

        body.addEventListener('click', async (e) => {
            const button = e.target.closest('button[data-action]');
            if (!button) return;

            const action = button.dataset.action;
            const jobId = button.dataset.jobId;

            if (action === 'delete') {
                const confirmed = await this.appClient.notifier.confirm("Are you sure you want to delete this video file and record?", { type: 'danger' });
                if (confirmed) {
                    try {
                        await this.appClient.rpcClient.call('VideoGeneratorRpc.deleteJob', { jobId });
                        this.appClient.notifier.success("Video deleted.");
                        loadJobs();
                    } catch (error) {
                        this.appClient.notifier.error(`Failed to delete: ${error.message}`);
                    }
                }
            } else if (action === 'cancel') {
                const confirmed = await this.appClient.notifier.confirm("Are you sure you want to cancel this video generation?");
                if (confirmed) {
                    try {
                        await this.appClient.rpcClient.call('VideoGeneratorRpc.cancelJob', { jobId });
                        this.appClient.notifier.info("Cancellation request sent.");
                        loadJobs();
                    } catch (error) {
                        this.appClient.notifier.error(`Failed to cancel: ${error.message}`);
                    }
                }
            }
        });

        generateBtn.addEventListener('click', () => {
            bsModal.hide();
            this.startVideoGeneration();
        });

        const progressHandler = (eventPayload) => {
            if (eventPayload.jobId) {
                loadJobs();
            }
        };
        this.appClient.events.server.on('video-generation-progress', progressHandler);

        modalEl.addEventListener('hidden.bs.modal', () => {
            this.appClient.events.server.off('video-generation-progress', progressHandler);
            modalEl.remove();
        }, { once: true });

        loadJobs();
    }

    async showGenerateAnimationsModal() {
        const modalId = `generate-animations-modal-${this.config.instanceId}`;
        document.getElementById(modalId)?.remove();

        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade generate-assets-modal'; // Reuse class for styling
        modalEl.id = modalId;
        modalEl.setAttribute('tabindex', '-1');
        modalEl.innerHTML = `
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Generate Video Animations</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="d-flex justify-content-center p-5"><div class="spinner-border" role="status"></div></div>
                    </div>
                    <div class="modal-footer">
                        <div class="form-check form-switch me-auto">
                            <input class="form-check-input" type="checkbox" id="force-anim-regen-switch">
                            <label class="form-check-label" for="force-anim-regen-switch">Force Regeneration</label>
                        </div>
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="start-animation-generation-btn">Generate Selected</button>
                    </div>
                </div>
            </div>`;
        document.body.appendChild(modalEl);
        const bsModal = new bootstrap.Modal(modalEl);
        bsModal.show();

        const body = modalEl.querySelector('.modal-body');
        try {
            const visualClips = (await this.appClient.rds.query('clips', c => c.projectId === this.currentProjectId && c.type === 'visual' && c.assetPath))
                .sort((a, b) => a.startTime - b.startTime);

            if (visualClips.length === 0) {
                body.innerHTML = `<div class="alert alert-info text-center">No visual clips with generated images were found. Please generate images for your visual clips first.</div>`;
                modalEl.querySelector('#start-animation-generation-btn').disabled = true;
                return;
            }

            body.innerHTML = `
                <div class="mb-3 d-flex align-items-center gap-2">
                    <button class="btn btn-sm btn-outline-secondary" id="select-all-anims">Select All</button>
                    <button class="btn btn-sm btn-outline-secondary" id="deselect-all-anims">Deselect All</button>
                    <button class="btn btn-sm btn-outline-warning" id="select-needing-gen">Select Needing Generation</button>
                </div>
                <ul class="list-group asset-list">
                    ${visualClips.map(clip => {
                // --- FIX: Add a tolerance to the duration check ---
                const DURATION_TOLERANCE_SECONDS = 0.1;
                const isTooShort = clip.actualDuration && (clip.duration > clip.actualDuration + DURATION_TOLERANCE_SECONDS);
                const needsGen = !clip.videoAssetPath || clip.isDirty || isTooShort;
                const isChecked = needsGen;
                const cacheBuster = clip.assetHash || Date.now();
                const imgSrc = `/${clip.assetPath}?v=${cacheBuster}`;
                const promptText = clip.prompt || 'No prompt';

                let statusHtml;
                if (isTooShort) {
                    statusHtml = `
                        <div>
                            <span class="badge bg-warning text-dark">Needs Extension</span>
                            <small class="d-block text-muted mt-1">
                                Clip: ${clip.duration.toFixed(1)}s, Video: ${clip.actualDuration.toFixed(1)}s
                            </small>
                        </div>`;
                } else if (needsGen) {
                    statusHtml = `<span class="badge bg-warning text-dark">Needs Generation</span>`;
                } else {
                    statusHtml = `<span class="badge bg-success">Ready</span>`;
                }

                return `
                        <li class="list-group-item">
                            <div class="d-flex align-items-center gap-3">
                                <input class="form-check-input" type="checkbox" value="${clip._id}" id="clip-anim-${clip._id}" ${isChecked ? 'checked' : ''}>
                                <img src="${imgSrc}" alt="Clip thumbnail" style="width: 96px; height: 54px; object-fit: cover; border-radius: var(--bs-border-radius-sm);">
                                <div class="flex-grow-1" style="min-width: 0;">
                                    <label class="form-check-label fw-bold" for="clip-anim-${clip._id}">
                                        Clip at ${clip.startTime.toFixed(2)}s
                                    </label>
                                    <p class="mb-0 text-muted small text-truncate" title="${this.escapeHtml(promptText)}">
                                        ${this.escapeHtml(promptText)}
                                    </p>
                                </div>
                                <div class="ms-auto text-end">${statusHtml}</div>
                            </div>
                        </li>`;
            }).join('')}
                </ul>`;

            modalEl.querySelector('#select-all-anims').addEventListener('click', () => modalEl.querySelectorAll('.form-check-input').forEach(cb => cb.checked = true));
            modalEl.querySelector('#deselect-all-anims').addEventListener('click', () => modalEl.querySelectorAll('.form-check-input').forEach(cb => cb.checked = false));
            modalEl.querySelector('#select-needing-gen').addEventListener('click', () => {
                visualClips.forEach(clip => {
                    // --- FIX: Use the same enhanced logic here ---
                    const DURATION_TOLERANCE_SECONDS = 0.5;
                    const isTooShort = clip.actualDuration && (clip.duration > clip.actualDuration + DURATION_TOLERANCE_SECONDS);
                    const needsGen = !clip.videoAssetPath || clip.isDirty || isTooShort;
                    const checkbox = modalEl.querySelector(`#clip-anim-${clip._id}`);
                    if (checkbox) checkbox.checked = needsGen;
                });
            });

            modalEl.querySelector('#start-animation-generation-btn').addEventListener('click', () => {
                const selectedClipIds = Array.from(modalEl.querySelectorAll('.form-check-input:checked')).map(cb => cb.value);
                const forceRegeneration = modalEl.querySelector('#force-anim-regen-switch').checked;

                if (selectedClipIds.length === 0) {
                    this.appClient.notifier.warn("No clips selected for animation generation.");
                    return;
                }

                bsModal.hide();
                const clientInstanceId = `video-anim-gen-${this.currentProjectId}-${Date.now()}`;
                this.appClient.notifier.showProgress({
                    title: 'Generating Video Animations',
                    message: 'Preparing to start generation...',
                });

                const progressHandler = (eventPayload) => {
                    if (eventPayload.instanceId !== clientInstanceId) return;
                    if (eventPayload.type === 'progress') {
                        this.appClient.notifier.setProgress({ progress: eventPayload.data.progress, message: eventPayload.progressMessage });
                    } else if (eventPayload.type === 'process-end') {
                        this.appClient.notifier.hideProgress();
                        this.appClient.events.server.off('video-animation-progress', progressHandler);
                        if (eventPayload.code === 0) {
                            this.appClient.notifier.success(eventPayload.progressMessage);
                        } else {
                            this.appClient.notifier.error(eventPayload.progressMessage);
                        }
                    }
                };

                this.appClient.events.server.on('video-animation-progress', progressHandler);
                this.appClient.rpcClient.call('VideoAnimationGeneratorRpc.generateAnimationsForClips', {
                    projectId: this.currentProjectId,
                    clipIds: selectedClipIds,
                    forceRegeneration
                }, { instanceId: clientInstanceId });
            });

        } catch (e) {
            this.log.error("Error preparing animation generation modal:", e);
            body.innerHTML = `<div class="alert alert-danger">Could not load visual clips to generate.</div>`;
        }
        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });
    }




    async startVideoGeneration() {
        const clipsWithoutAssets = this.orderedClips.filter(clip => {
            const assetRequired = ['visual', 'dialogue', 'sfx', 'bgMusic', 'ambient'].includes(clip.type);
            return assetRequired && !clip.assetPath;
        });

        if (clipsWithoutAssets.length > 0) {
            const clipTypes = [...new Set(clipsWithoutAssets.map(c => c.type))].join(', ');
            this.appClient.notifier.error(`Cannot generate video. The following clip types are missing assets: ${clipTypes}. Please use "Generate All Assets" first.`);
            return;
        }

        this.appClient.notifier.info("Starting video generation process...");

        const clientInstanceId = `video-gen-${this.currentProjectId}-${Date.now()}`;

        this.appClient.notifier.showProgress({
            title: 'Generating Video',
            message: 'Preparing video generation job...',
            isDialog: true
        });

        const progressHandler = (eventPayload) => {
            if (eventPayload.instanceId !== clientInstanceId) return;

            if (eventPayload.type === 'progress') {
                this.appClient.notifier.setProgress({
                    progress: eventPayload.data.progress,
                    message: eventPayload.progressMessage
                });
            } else if (eventPayload.type === 'process-end') {
                this.appClient.notifier.hideProgress();
                this.appClient.events.server.off('video-generation-progress', progressHandler);
                if (eventPayload.code === 0) {
                    this.appClient.notifier.success(eventPayload.progressMessage);
                    if (eventPayload.data?.videoPath) {
                        const videoUrl = `/${eventPayload.data.videoPath}`;
                        this.appClient.notifier.notify(`Video ready! <a href="${videoUrl}" target="_blank">Click to view</a>`, {
                            type: 'success',
                            duration: 10000,
                            isHtml: true
                        });
                    }
                } else {
                    this.appClient.notifier.error(eventPayload.progressMessage);
                }
            }
        };

        this.appClient.events.server.on('video-generation-progress', progressHandler);

        try {
            await this.appClient.rpcClient.call('VideoGeneratorRpc.generateProjectVideo', {
                projectId: this.currentProjectId
            }, { instanceId: clientInstanceId });
        } catch (error) {
            this.appClient.notifier.hideProgress();
            this.appClient.events.server.off('video-generation-progress', progressHandler);
            this.appClient.notifier.error(`Failed to start video generation: ${error.message}`);
        }
    }


    async handleDeleteClip(clipId) {
        const confirmed = await this.appClient.notifier.confirm("Are you sure you want to delete this clip?", { type: 'danger' });
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('ClipRpc.delete', { _id: clipId });
                this.appClient.notifier.success("Clip deleted.");
                if (this.selectedClipId === clipId) this.selectedClipId = null;
            } catch (error) {
                this.appClient.notifier.error(`Failed to delete clip: ${error.message}`);
            }
        }
    }

    showPreviewModal(assetPath, assetHash) {
        if (this.previewModalInstance) this.previewModalInstance.dispose();
        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade';
        const cacheBuster = assetHash || Date.now();
        const src = `/${assetPath}?v=${cacheBuster}`;
        modalEl.innerHTML = `<div class="modal-dialog modal-dialog-centered modal-lg"><div class="modal-content"><div class="modal-body" style="text-align: center;"><img src="${src}" alt="Preview" style="max-width: 100%; max-height: 80vh; border-radius: var(--bs-border-radius);"></div></div></div>`;
        document.body.appendChild(modalEl);
        this.previewModalInstance = new bootstrap.Modal(modalEl);
        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });
        this.previewModalInstance.show();
    }

    handleAudioPreview(event, assetPath) {
        const button = event.currentTarget;
        const icon = button.querySelector('i');

        // --- START OF FIX: Normalize audio path ---

        let audioSrc = assetPath;
        if (!audioSrc.startsWith('http')) {
            if (audioSrc.startsWith('global/')) {
                audioSrc = `/media/${audioSrc}`;
            }
            else if (!audioSrc.startsWith('/')) {
                if (!audioSrc.startsWith('media/')) {
                    audioSrc = `/media/${audioSrc}`;
                }
                else {
                    audioSrc = `/${audioSrc}`;
                }
            }
        }

        // --- END OF FIX ---

        const isPlaying = this.audioPlayer.src.endsWith(audioSrc) && !this.audioPlayer.paused;

        // Stop any currently playing audio
        this.audioPlayer.pause();
        if (icon) icon.className = 'bi bi-play-circle-fill';

        if (isPlaying) {
            this.audioPlayer.currentTime = 0;
        } else {
            if (icon) icon.className = 'bi bi-hourglass-split';
            this.audioPlayer.src = audioSrc;
            const resetBtn = () => { if (icon) icon.className = 'bi bi-play-circle-fill' };
            this.audioPlayer.onplaying = () => { if (icon) icon.className = 'bi bi-stop-circle-fill' };
            this.audioPlayer.onended = resetBtn;
            this.audioPlayer.onerror = () => { this.appClient.notifier.error("Could not play audio."); resetBtn(); };
            this.audioPlayer.play().catch(e => { this.appClient.notifier.error("Could not play audio."); resetBtn(); });
        }
    }


    onRdsUpdate(eventName, data) {
        this.log.info(`RDS update received for '${eventName}'. Reloading editor data.`);
        this.loadData();
    }


    async initAfterLogin() {
        const selectedProject = this.appClient.getSharedData('selectedProject');
        await this.handleProjectSelected({ projectData: selectedProject });
    }

    async handleProjectSelected({ projectData }) {
        const newProjectId = projectData ? projectData._id : null;
        if (this.currentProjectId === newProjectId) return;
        this.currentProjectId = newProjectId;
        this.resetState();
        await this.loadData();
    }


    resetState() {
        this.projectSettings = null;
        this.orderedClips = [];
        this.selectedClipId = null;
        this.currentTime = 0;
        this.pause();
    }

    async loadData() {
        if (!this.currentProjectId) {
            this.updateUI();
            return;
        }
        const scrollLeft = this.timeline?.scrollContainer?.scrollLeft || 0;
        const scrollTop = this.timeline?.scrollContainer?.scrollTop || 0;
        this.updateUI();
        try {
            const allClips = await this.appClient.rds.query('clips', clip => clip.projectId === this.currentProjectId);
            const projectSettings = await this.appClient.rds.query('projectSettings', setting => setting.projectId === this.currentProjectId);

            this.projectSettings = projectSettings[0];
            this.orderedClips = allClips.sort((a, b) => a.startTime - b.startTime);

        } catch (error) {
            this.log.error("Failed to load project data from RDS:", error);
            this.appClient.notifier.error("Could not load project data for the editor.");
            this.projectSettings = null;
            this.orderedClips = [];

        } finally {
            this.isLoading = false;
            this.updateUI();
            if (this.timeline?.scrollContainer) {
                this.timeline.scrollContainer.scrollLeft = scrollLeft;
                this.timeline.scrollContainer.scrollTop = scrollTop;
                this.timeline.syncScroll();
            }
        }
    }

    async handleReorganizeTracks() {
        if (!this.currentProjectId) return;
        this.appClient.notifier.showProgress({
            title: 'AI Organizing Timeline',
            message: 'Analyzing and synchronizing your video clips...',
            isDialog: true
        });
        try {
            await this.appClient.rpcClient.call('ClipRpc.reorganizeTracks', { projectId: this.currentProjectId });
            this.appClient.notifier.success("Timeline reorganized by AI!");
        } catch (error) {
            this.log.error("Failed to reorganize tracks with AI:", error);
            this.appClient.notifier.error(`AI reorganization failed: ${error.message}`);
        } finally {
            this.appClient.notifier.hideProgress();
        }
    }

    play() {
        if (this.isPlaying) return;
        this.isPlaying = true;
        this.timeline?.setPlayingState(true);
        let lastTime = performance.now();
        const animate = (now) => {
            if (!this.isPlaying) return;
            const deltaTime = (now - lastTime) / 1000;
            lastTime = now;
            this.currentTime += deltaTime;
            const totalDuration = this.orderedClips.reduce((sum, clip) => Math.max(sum, clip.startTime + clip.duration), 0);
            if (this.currentTime >= totalDuration) this.currentTime = 0;
            this.updatePlaybackTime(this.currentTime);
            this.animationFrameId = requestAnimationFrame(animate);
        };
        this.animationFrameId = requestAnimationFrame(animate);
    }

    pause() {
        this.isPlaying = false;
        if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
        this.timeline?.setPlayingState(false);
        this.previewer?.stopAllAudio();
    }

    updatePlaybackTime(newTime) {
        this.currentTime = newTime;
        this.timeline?.updatePlayhead(this.currentTime);
        this.previewer?.update(this.currentTime, this.isPlaying);
    }

    async showEditClipModal(clipId) {
        if (this.clipEditModal) this.clipEditModal.destroy();
        const clipData = clipId ? this.orderedClips.find(c => c._id === clipId) : null;
        const isNew = !clipData;
        const formConfig = getClipEditorFormConfig();
        this.clipEditModal = await this.createManagedComponent("DataForm", null, {
            ...formConfig, instanceId: `video-editor-clip-form-${Date.now()}`, layout: 'dialog',
            title: isNew ? 'Add Clip' : 'Edit Clip', actions: { save: true, cancel: true },
        });
        this.clipEditModal.show();
        if (clipData) this.clipEditModal.setData(clipData);
        this.clipEditModal.on('dataSaved', async (newClipData) => {
            try {
                await this.appClient.rpcClient.call('ClipRpc.create', { ...newClipData, projectId: this.currentProjectId });
                this.appClient.notifier.success("Clip created.");
            } catch (error) { this.appClient.notifier.error(`Failed to create clip: ${error.message}`); }
            finally { this.clipEditModal.destroy(); this.clipEditModal = null; }
        });
        this.clipEditModal.on('dataUpdated', async (updatedClipData) => {
            try {
                await this.appClient.rpcClient.call('ClipRpc.update', { _id: clipId, ...updatedClipData });
                this.appClient.notifier.success("Clip updated.");
            } catch (error) { this.appClient.notifier.error(`Failed to update clip: ${error.message}`); }
            finally { this.clipEditModal.destroy(); this.clipEditModal = null; }
        });
        this.clipEditModal.on('cancel', () => {
            this.selectedClipId = null;
            this.timeline.setData(this.orderedClips, null);
            if (this.clipEditModal) { this.clipEditModal.destroy(); this.clipEditModal = null; }
        });
    }

    async handleGenerateClipAsset(clipId, force = false) {
        this.appClient.notifier.info(`Generating asset for clip...`);
        try {
            await this.appClient.rpcClient.call('AssetGenerationRpc.generateClipAsset', { clipId, forceRegeneration: force });
            this.appClient.notifier.success(`Asset for clip generated successfully.`);
        } catch (error) {
            this.appClient.notifier.error(`Failed to generate clip asset: ${error.message}`);
        }
    }

    handleJobProgress(payload) {
        if (!this.aiProgressMonitor || !this.aiProgressMonitor.jobId || this.aiProgressMonitor.jobId !== payload.jobId) {
            return; // This event is not for the currently monitored job
        }

        const { eventName, payload: eventPayload } = payload;
        let logEntry = `Event: ${eventName}`;
        let progress = null; // Set to null to keep it indeterminate
        let message = this.aiProgressMonitor.messageEl.textContent; // Keep current message by default
        let status = 'running';

        switch (eventName) {
            case 'agentic.job.start':
                logEntry = `Job started. Goal: ${eventPayload.goal}`;
                message = 'Starting AI task...';
                break;
            case 'agentic.task.start':
                logEntry = `Starting task: ${eventPayload.toolName}`;
                message = `Executing: ${eventPayload.toolName}`;
                break;
            case 'agentic.orchestrator.response':
                logEntry = `AI thought: ${eventPayload.response}`;
                message = "AI is planning next steps...";
                break;
            case 'agentic.job.complete':
                logEntry = `Job completed successfully.Final message: ${eventPayload.result}`;
                message = "Finished!";
                progress = 100; // Final state
                status = 'completed';
                break;
            case 'agentic.job.fail':
            case 'agentic.job.cancel':
                logEntry = `Job failed or was cancelled.Reason: ${eventPayload.error || 'Cancelled by user'}`;
                message = "Job Failed/Cancelled";
                progress = 100; // Final state
                status = eventName === 'agentic.job.cancel' ? 'cancelled' : 'failed';
                break;
        }
        this.aiProgressMonitor.update({ progress, message, status, logEntry });
    }


    // --- NEW: Handler for cancelling a job ---
    async handleCancelAiJob(jobId) {
        if (!jobId) return;
        const confirmed = await this.appClient.notifier.confirm("Are you sure you want to cancel this AI task?");
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('StoryRpc.cancelStoryGeneration', { jobId });
                this.appClient.notifier.info("Cancellation request sent.");
                // The 'agentic.job.cancel' event from the server will update the UI state.
            } catch (error) {
                this.appClient.notifier.error(`Failed to send cancellation: ${error.message}`);
            }
        }
    }


    async showTimelineToolsModal() {
        const modalId = `timeline-tools-modal-${this.config.instanceId}`;
        document.getElementById(modalId)?.remove();

        const modalEl = document.createElement('div');
        modalEl.className = 'modal fade';
        modalEl.id = modalId;
        modalEl.setAttribute('tabindex', '-1');
        modalEl.innerHTML = `
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Timeline Tools</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p class="text-muted small">Apply automated actions to the timeline.</p>
                        <div class="list-group">
                            <div class="list-group-item">
                                <div class="d-flex w-100 justify-content-between">
                                    <h6 class="mb-1">Close Gaps in Visuals</h6>
                                    <button class="btn btn-primary btn-sm" id="run-close-gaps">Run</button>
                                </div>
                                <p class="mb-1 small">Adjusts all visual clips to eliminate empty space between them.</p>
                                <div class="form-check form-switch mt-2">
                                    <input class="form-check-input" type="checkbox" id="shift-subsequent-clips" checked>
                                    <label class="form-check-label small" for="shift-subsequent-clips">
                                        Shift overlapping clips (dialogue, SFX, etc.)
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modalEl);
        const bsModal = new bootstrap.Modal(modalEl);
        bsModal.show();

        modalEl.querySelector('#run-close-gaps').addEventListener('click', () => {
            const shiftSubsequent = modalEl.querySelector('#shift-subsequent-clips').checked;
            this.handleCloseVisualGaps(shiftSubsequent);
            bsModal.hide();
        });

        modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove(), { once: true });
    }
    // --- END OF FIX ---

    // --- START OF FIX: New method with the core logic for closing gaps ---
    async handleCloseVisualGaps(shiftSubsequentClips) {
        this.appClient.notifier.info("Analyzing timeline to close gaps...");

        const visualClips = this.orderedClips
            .filter(c => c.type === 'visual')
            .sort((a, b) => a.startTime - b.startTime);

        if (visualClips.length < 2) {
            this.appClient.notifier.info("Not enough visual clips to find gaps.");
            return;
        }

        const updates = [];
        let currentTime = 0;
        let totalShift = 0;

        for (let i = 0; i < visualClips.length; i++) {
            const clip = visualClips[i];
            const originalStartTime = clip.startTime;

            // The first clip determines the starting point.
            if (i === 0) {
                currentTime = originalStartTime;
            }

            // If there's a gap, schedule an update for this clip.
            if (originalStartTime > currentTime) {
                updates.push({ _id: clip._id, startTime: currentTime });
                clip.startTime = currentTime; // Update in-memory for next iteration
            }

            // If subsequent clips need to be shifted, calculate the shift for other tracks.
            if (shiftSubsequentClips) {
                const shiftAmount = clip.startTime - originalStartTime;
                if (Math.abs(shiftAmount) > 0.01) {
                    // Find all non-visual clips that start within the original bounds of this visual clip
                    this.orderedClips.forEach(otherClip => {
                        if (otherClip.type !== 'visual' &&
                            otherClip.startTime >= originalStartTime &&
                            otherClip.startTime < (originalStartTime + clip.duration)) {

                            // Check if this clip has already been scheduled for an update to avoid conflicts
                            if (!updates.some(u => u._id === otherClip._id)) {
                                updates.push({ _id: otherClip._id, startTime: otherClip.startTime + shiftAmount });
                            }
                        }
                    });
                }
            }

            // Move the playhead to the end of the current clip for the next iteration.
            currentTime = clip.startTime + clip.duration;
        }

        if (updates.length === 0) {
            this.appClient.notifier.success("No gaps found in the visual track.");
            return;
        }

        try {
            await this.appClient.rpcClient.call('ClipRpc.updateMany', { updates });
            this.appClient.notifier.success(`Successfully closed gaps and applied ${updates.length} update(s).`);
        } catch (error) {
            this.appClient.notifier.error(`Failed to apply timeline changes: ${error.message}`);
            this.loadData(); // Reload to revert optimistic changes
        }
    }
    // --- END OF FIX ---

    // --- START OF FIX: New method to handle resizing clips to their media length ---
    async handleResizeClipToMedia(clipId) {
        const clip = this.orderedClips.find(c => c._id === clipId);
        if (!clip || !clip.actualDuration) {
            this.appClient.notifier.warn("Clip has no media or duration information.");
            return;
        }

        if (Math.abs(clip.duration - clip.actualDuration) < 0.1) {
            this.appClient.notifier.info("Clip duration already matches media length.");
            return;
        }

        try {
            await this.appClient.rpcClient.call('ClipRpc.update', {
                _id: clipId,
                duration: clip.actualDuration
            });
            this.appClient.notifier.success(`Clip resized to ${clip.actualDuration.toFixed(2)}s.`);
        } catch (error) {
            this.appClient.notifier.error(`Failed to resize clip: ${error.message}`);
        }
    }


    destroy() {
        if (this._boundHandleKeyDown) {
            document.removeEventListener('keydown', this._boundHandleKeyDown);
        }
        this.appClient.events.local.off('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.server.off('agentic-job-progress', this.handleJobProgress.bind(this)); // <-- UNBIND
        this.pause();
        this.previewModalInstance?.dispose();
        this.audioPlayer.pause();
        this.audioPlayer.src = '';
        this.previewer?.destroy();
        this.timeline?.destroy();
        this.clipEditModal?.destroy();
        this.storyModalForm?.destroy();
        this.settingsModalForm?.destroy();
        this.trackSettingsPanel?.destroy();
        this.aiModifyModal?.destroy();
        this.aiProgressMonitor?.destroy();
        super.destroy();
    }
}